/**
 * To Be Invoiced card — overage entries rolled up by person and role.
 *
 * The card lists one row per time entry; whoever prepares the invoice wants
 * "how many overage hours did each person work in each role, and what is that
 * worth". This is that roll-up, kept pure so the arithmetic is testable
 * without rendering.
 *
 * ALL ARITHMETIC IS IN INTEGERS. Hours are summed as hundredths and money as
 * cents (every figure arrives already rounded to 2 decimals by the backend),
 * so a row is the exact sum of the entries it covers and the Total is the
 * exact sum of the rows — never a float that prints as 53.99999999.
 *
 * A person+role billed at more than one rate (a rate changed mid-period) is
 * split into one row per rate rather than shown as a blended "mixed" figure,
 * so hours x rate = billing stays readable on every row.
 *
 * Billing is the sum of the entries' own `billing`, so it agrees with the
 * entry list below it. The backend rounds the header total once per role
 * (hours x rate), so the header may differ from this Total by a cent or two
 * on rounding alone.
 */
import type { OverageEntryDetail } from '../services/api/toBeInvoiced';

export type OverageSummaryEntry = Pick<
  OverageEntryDetail,
  'userId' | 'userName' | 'roleId' | 'roleName' | 'overageSlice' | 'hourlyRate' | 'billing' | 'hasRate'
>;

export interface OverageSummaryRow {
  userId: number;
  userName: string;
  roleId: number | null;
  roleName: string | null;
  /** Hours, 2 decimals: the exact sum of the entries' overage slices. */
  hours: number;
  /** Null when the entries carry no rate. */
  hourlyRate: number | null;
  /** Null when `hourlyRate` is null (billing is not computable). */
  billing: number | null;
}

export interface OverageSummary {
  rows: OverageSummaryRow[];
  totalHours: number;
  /** Sum of the rows that have a rate. */
  totalBilling: number;
  /** True when some hours have no rate and so contribute no billing. */
  hasUnratedHours: boolean;
}

const toHundredths = (n: number): number => Math.round(n * 100);
const fromHundredths = (n: number): number => n / 100;

export function summarizeOverageByPersonRole(
  entries: ReadonlyArray<OverageSummaryEntry>,
): OverageSummary {
  interface Acc {
    userId: number;
    userName: string;
    roleId: number | null;
    roleName: string | null;
    rateCents: number | null;
    hours: number;
    billing: number;
  }
  const groups = new Map<string, Acc>();

  for (const e of entries) {
    const rateCents = e.hasRate ? toHundredths(e.hourlyRate) : null;
    const key = `${e.userId}|${e.roleId ?? 'none'}|${rateCents ?? 'norate'}`;
    let acc = groups.get(key);
    if (!acc) {
      acc = {
        userId: e.userId,
        userName: e.userName,
        roleId: e.roleId,
        roleName: e.roleName,
        rateCents,
        hours: 0,
        billing: 0,
      };
      groups.set(key, acc);
    }
    acc.hours += toHundredths(e.overageSlice);
    if (e.hasRate) acc.billing += toHundredths(e.billing);
  }

  const accs = [...groups.values()].sort((a, b) => {
    const byUser = a.userName.localeCompare(b.userName);
    if (byUser !== 0) return byUser;
    if (a.userId !== b.userId) return a.userId - b.userId;
    // No role sorts last within the person.
    if ((a.roleName === null) !== (b.roleName === null)) return a.roleName === null ? 1 : -1;
    const byRole = (a.roleName ?? '').localeCompare(b.roleName ?? '');
    if (byRole !== 0) return byRole;
    return (a.rateCents ?? -1) - (b.rateCents ?? -1);
  });

  let totalHours = 0;
  let totalBilling = 0;
  let hasUnratedHours = false;
  const rows = accs.map<OverageSummaryRow>((acc) => {
    totalHours += acc.hours;
    if (acc.rateCents !== null) totalBilling += acc.billing;
    else if (acc.hours !== 0) hasUnratedHours = true;
    return {
      userId: acc.userId,
      userName: acc.userName,
      roleId: acc.roleId,
      roleName: acc.roleName,
      hours: fromHundredths(acc.hours),
      hourlyRate: acc.rateCents === null ? null : fromHundredths(acc.rateCents),
      billing: acc.rateCents === null ? null : fromHundredths(acc.billing),
    };
  });

  return {
    rows,
    totalHours: fromHundredths(totalHours),
    totalBilling: fromHundredths(totalBilling),
    hasUnratedHours,
  };
}
